import React, { Component, Fragment } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ListViewAs from 'components/cosmos/menu/components/listViewAs';
import { ListItemLink } from 'components/list';
import ClassIcon from 'components/svg/icons/class.svg';
import CommunicateIcon from 'components/svg/icons/communicate.svg';
import CommunityIcon from 'components/svg/icons/community-menu.svg';
import ConfigurationIcon from 'components/svg/icons/configuration.svg';
import ContentIcon from 'components/svg/icons/content-menu.svg';
import HomeIcon from 'components/svg/icons/home.svg';
import ReportIcon from 'components/svg/icons/report.svg';
import SalesIcon from 'components/svg/icons/sales-menu.svg';
import StudentIcon from 'components/svg/icons/student.svg';
import { translate } from 'components/translate';
import { inject, observer } from 'mobx-react';
import {
  EVENT_ACTIONS,
  EVENTS_INTERACTIONS,
  EVENTS_CATEGORY,
  EVENTS_LABELS
} from 'modules/app/services/datahub/constants';
import DatahubService from 'modules/app/services/datahub/DatahubService';
import { buildInteractionEventData } from 'modules/app/services/datahub/helper';
import {
  DatahubEventsService,
  DatahubViewHomeService
} from 'modules/app/services/datahubEvents';
import AuthService from 'modules/auth/AuthService';
import { CommunityService } from 'modules/community';
import { ContentPageService } from 'modules/content';
import { FeatureToggles, FeatureToggleService } from 'modules/featureToggle';
import MembershipService from 'modules/membership/MembershipService';
import SidebarBadge from 'modules/navigation/components/sidebar/sidebarBadge';
import DisabledOnOnlineEvent from 'modules/onlineEvent/components/DisabledOnOnlineEvent';
import { SalesService } from 'modules/salesManager';
import PropTypes from 'prop-types';

import ConfigService from '../../../configManager/ConfigService';
import MembershipEventService from '../../../membershipEvent/MembershipEventService';
import SidebarEvents from '../../events';

import './SidebarContent.scss';

const commom = {
  category: EVENTS_CATEGORY.VERTICAL_MENU.NAME,
  action: EVENTS_INTERACTIONS.CLICK
};

@translate()
@inject((stores) => ({
  communityComments: stores.CommunityStore.communityComments,
  showableOfferAlert: stores.SalesStore.showableDashboardOfferAlert
}))
@observer
export default class SidebarContent extends Component {
  static propTypes = {
    location: PropTypes.object,
    communityComments: PropTypes.number,
    showableOfferAlert: PropTypes.bool
  };

  links = {
    dashboard: 'dashboard',
    content: 'content',
    ctaCommunities: 'ctaCommunities',
    classManager: 'classManager',
    userManager: 'userManager',
    broadcastManager: 'broadcastManager',
    reports: 'reports',
    community: 'community',
    salesManager: 'salesManager',
    configManager: 'configManager'
  };

  componentDidMount() {
    if (!AuthService.isEditor()) {
      CommunityService.getCommunityNotifications();

      !AuthService.isModerator() && SalesService.fetchDashboardOfferAlert();
    }

    ContentPageService.init();
    MembershipEventService.init();
  }

  showNewFeatureBadge() {
    if (ConfigService.highlightTabs.length > 0) {
      return <SidebarBadge>{this.props.t('general.new')}</SidebarBadge>;
    }

    return null;
  }

  renderSalesBadge = () => {
    const { showableOfferAlert } = this.props;

    if (!showableOfferAlert) {
      return null;
    }

    return (
      <SidebarBadge>
        <FontAwesomeIcon icon={['fal', 'exclamation-circle']} />
      </SidebarBadge>
    );
  };

  renderEditorMenu(menuWithPreview = false) {
    return (
      <>
        {menuWithPreview ? this.renderPreviewAs() : null}

        <ListItemLink
          to={MembershipService.getLink(this.links.content)}
          icon={ContentIcon}
          iconAlt="general.content"
          location={this.props.location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Content' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.CONTENT
              })
            });
          }}
        >
          {this.props.t('general.content', { checkOnlineEvent: true })}
        </ListItemLink>
      </>
    );
  }

  renderModeratorMenu(menuWithPreview = false) {
    const { communityComments } = this.props;

    const handleCommentClick = () => {
      const schema = new DatahubViewHomeService();

      DatahubEventsService.sendEvent({ schema });

      SidebarEvents.sendClick({ label: 'Comments' });

      DatahubService.sendEvent({
        action: EVENT_ACTIONS.INTERACT,
        event: buildInteractionEventData({
          ...commom,
          label: EVENTS_LABELS.VERTICAL_MENU.COMMENTS
        })
      });
    };

    return (
      <>
        {menuWithPreview ? this.renderPreviewAs() : null}

        <ListItemLink
          to={MembershipService.getLink(this.links.community)}
          icon={CommunityIcon}
          iconAlt="general.comments"
          location={this.props.location}
          badge={
            communityComments ? (
              <SidebarBadge>{communityComments}</SidebarBadge>
            ) : null
          }
          onClick={handleCommentClick}
        >
          {this.props.t('general.comments')}
        </ListItemLink>
      </>
    );
  }

  renderDashboardItem() {
    return (
      AuthService.hasDashboardEnabled && (
        <DisabledOnOnlineEvent>
          <ListItemLink
            to={MembershipService.getLink(this.links.dashboard)}
            icon={HomeIcon}
            iconAlt="general.home"
            location={this.props.location}
            onClick={() => {
              SidebarEvents.sendClick({ label: 'Home' });

              DatahubService.sendEvent({
                action: EVENT_ACTIONS.INTERACT,
                event: buildInteractionEventData({
                  ...commom,
                  label: EVENTS_LABELS.VERTICAL_MENU.HOME
                })
              });
            }}
          >
            {this.props.t('general.home')}
          </ListItemLink>
        </DisabledOnOnlineEvent>
      )
    );
  }

  renderPreviewAs() {
    return <ListViewAs />;
  }

  get defaultMenu() {
    const { location } = this.props;

    return (
      <>
        {this.renderPreviewAs()}
        {this.renderDashboardItem()}
        {this.renderEditorMenu()}
        {FeatureToggleService.isEnabled(FeatureToggles.CtaCommunities) && (
          <ListItemLink
            to={MembershipService.getLink(this.links.ctaCommunities)}
            icon={CommunityIcon}
            iconAlt="general.communities"
            location={location}
            onClick={() => {
              SidebarEvents.sendClick({ label: 'Communities' });

              DatahubService.sendEvent({
                action: EVENT_ACTIONS.INTERACT,
                event: buildInteractionEventData({
                  ...commom,
                  label: EVENTS_LABELS.VERTICAL_MENU.COMMUNITIES
                })
              });
            }}
          >
            {this.props.t('general.communities', { checkOnlineEvent: true })}
            <SidebarBadge>{this.props.t('general.new')}</SidebarBadge>
          </ListItemLink>
        )}
        <ListItemLink
          to={MembershipService.getLink(this.links.classManager)}
          icon={ClassIcon}
          iconAlt="general.classes"
          location={location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Groups' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.GROUPS
              })
            });
          }}
        >
          {this.props.t('general.classes', { checkOnlineEvent: true })}
        </ListItemLink>
        <ListItemLink
          to={MembershipService.getLink(this.links.userManager)}
          icon={StudentIcon}
          iconAlt="general.users"
          location={location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Users' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.USERS
              })
            });
          }}
        >
          {this.props.t('general.users', { checkOnlineEvent: true })}
        </ListItemLink>
        <ListItemLink
          to={MembershipService.getLink(this.links.broadcastManager)}
          icon={CommunicateIcon}
          iconAlt="general.broadcast"
          location={location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Message' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.MESSAGE
              })
            });
          }}
        >
          {this.props.t('general.broadcast')}
        </ListItemLink>

        {this.renderModeratorMenu()}

        <DisabledOnOnlineEvent>
          <ListItemLink
            to={MembershipService.getLink(this.links.reports)}
            icon={ReportIcon}
            iconAlt="general.reports"
            location={location}
            onClick={() => {
              SidebarEvents.sendClick({ label: 'Reports' });

              DatahubService.sendEvent({
                action: EVENT_ACTIONS.INTERACT,
                event: buildInteractionEventData({
                  ...commom,
                  label: EVENTS_LABELS.VERTICAL_MENU.REPORTS
                })
              });
            }}
          >
            {this.props.t('general.reports')}
          </ListItemLink>
        </DisabledOnOnlineEvent>
        <ListItemLink
          to={MembershipService.getLink(this.links.salesManager)}
          icon={SalesIcon}
          iconAlt="general.sales"
          location={location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Sales' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.SALES
              })
            });
          }}
        >
          {this.props.t('general.sales')}
          {this.renderSalesBadge()}
        </ListItemLink>
        <ListItemLink
          to={MembershipService.getLink(this.links.configManager)}
          icon={ConfigurationIcon}
          iconAlt="general.configs"
          location={location}
          onClick={() => {
            SidebarEvents.sendClick({ label: 'Settings' });

            DatahubService.sendEvent({
              action: EVENT_ACTIONS.INTERACT,
              event: buildInteractionEventData({
                ...commom,
                label: EVENTS_LABELS.VERTICAL_MENU.SETTINGS
              })
            });
          }}
        >
          {this.props.t('general.configs')}
          {this.showNewFeatureBadge()}
        </ListItemLink>
      </>
    );
  }

  renderMenu() {
    if (AuthService.isEditor()) {
      return this.renderEditorMenu(true);
    }

    if (AuthService.isModerator()) {
      return this.renderModeratorMenu(true);
    }

    return this.defaultMenu;
  }

  render() {
    return <div className="sidebar__content">{this.renderMenu()}</div>;
  }
}
